document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

(() => {
  const doc = document;
  const body = doc.body;
  const liveRegion = () => doc.getElementById('AlignLiveRegion');
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || (window.Align && window.Align.routes.root) || '/';
  let returnFocus = null;

  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 30);
  };

  const getFocusable = (container) => [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hidden && el.offsetParent !== null);

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = getFocusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const openMenu = (button) => {
    const menu = doc.querySelector('[data-mobile-menu]');
    if (!menu) return;
    returnFocus = button;
    menu.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    button.setAttribute('aria-label', 'Close navigation');
    body.classList.add('is-locked');
    getFocusable(menu)[0]?.focus();
  };

  const closeMenu = () => {
    const menu = doc.querySelector('[data-mobile-menu]');
    const button = doc.querySelector('[data-menu-trigger]');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    button?.setAttribute('aria-expanded', 'false');
    button?.setAttribute('aria-label', 'Open navigation');
    body.classList.remove('is-locked');
    returnFocus?.focus();
  };

  const openCart = (trigger) => {
    const drawer = doc.querySelector('[data-cart-drawer]');
    const scrim = doc.querySelector('[data-cart-scrim]');
    if (!drawer || !scrim) return;
    returnFocus = trigger || doc.querySelector('[data-cart-trigger]');
    drawer.hidden = false;
    scrim.hidden = false;
    window.requestAnimationFrame(() => {
      drawer.classList.add('open');
      scrim.classList.add('open');
    });
    body.classList.add('is-locked');
    getFocusable(drawer)[0]?.focus();
  };

  const closeCart = () => {
    const drawer = doc.querySelector('[data-cart-drawer]');
    const scrim = doc.querySelector('[data-cart-scrim]');
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('open');
    scrim?.classList.remove('open');
    window.setTimeout(() => {
      drawer.hidden = true;
      if (scrim) scrim.hidden = true;
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 340);
    body.classList.remove('is-locked');
    returnFocus?.focus();
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents, currency = 'EGP') => new Intl.NumberFormat('en-EG', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format((Number(cents) || 0) / 100).replace(/[\u200E\u200F]/g, '').replace(/\u00A0/g, ' ');

  const renderCart = (cart) => {
    const lines = doc.querySelector('[data-cart-lines]');
    const footer = doc.querySelector('[data-cart-footer]');
    const total = doc.querySelector('[data-cart-total]');
    if (!lines || !footer) return false;

    if (!cart.item_count) {
      lines.innerHTML = `<div class="cart-empty"><p>Your cart is empty.</p><a class="button secondary" href="${routeRoot()}collections/all">View The Align</a></div>`;
      footer.hidden = true;
    } else {
      lines.innerHTML = cart.items.map((item, index) => {
        const title = escapeHtml(item.product_title || item.title);
        const url = escapeHtml(item.url || '#');
        const image = item.image ? `<img src="${escapeHtml(item.image)}" width="152" height="152" loading="lazy" alt="">` : '';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="li-meta">${escapeHtml(item.variant_title)}</p>` : '';
        return `<article class="line-item" data-line-item="${index + 1}">
          <a class="line-media" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>
          <div class="line-copy">
            <h3><a href="${url}">${title}</a></h3>${variant}
            <div class="li-row">
              <div class="qty" aria-label="Quantity for ${title}">
                <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
                <span aria-live="polite">${item.quantity}</span>
                <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <span class="li-price">${formatMoney(item.final_line_price, cart.currency)}</span>
            </div>
            <button class="li-remove" type="button" data-cart-change data-line="${index + 1}" data-quantity="0">Remove</button>
          </div>
        </article>`;
      }).join('');
      footer.hidden = false;
      if (total) total.textContent = formatMoney(cart.total_price, cart.currency);
    }

    doc.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    doc.querySelectorAll('[data-cart-trigger]').forEach((button) => button.setAttribute('aria-label', `Open cart, ${cart.item_count} items`));
    return true;
  };

  const refreshCart = async ({ open = false } = {}) => {
    const cartResponse = await fetch(`${routeRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!cartResponse.ok) throw new Error('Could not read the updated cart.');
    const cart = await cartResponse.json();
    renderCart(cart);
    if (open) openCart();
    return cart;
  };

  const addToCart = async (form) => {
    const submit = form.querySelector('[data-add-to-cart]');
    const label = form.querySelector('[data-add-label]');
    const error = form.querySelector('[data-product-error]');
    const original = label?.textContent;
    if (error) { error.hidden = true; error.textContent = ''; }
    if (submit) submit.disabled = true;
    if (label) label.textContent = 'Adding…';
    try {
      const variantId = form.elements.namedItem('id')?.value;
      const quantity = Math.max(1, Number(form.elements.namedItem('quantity')?.value) || 1);
      if (!variantId) throw new Error('Please select an available product option.');
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added.');
      try {
        await refreshCart({ open: true });
        announce('Product added to cart.');
      } catch (_) {
        announce('Product added. Open the cart to view it.');
        openCart();
      }
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
      announce(err.message);
    } finally {
      if (submit) submit.disabled = false;
      if (label) label.textContent = original;
    }
  };

  const changeCartLine = async (button) => {
    button.disabled = true;
    try {
      const response = await fetch(`${routeRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(button.dataset.line), quantity: Number(button.dataset.quantity) })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'The cart could not be updated.');
      renderCart(data);
      announce('Cart updated.');
    } catch (err) {
      button.disabled = false;
      announce(err.message);
    }
  };

  doc.addEventListener('click', (event) => {
    const menuButton = event.target.closest('[data-menu-trigger]');
    if (menuButton) {
      menuButton.getAttribute('aria-expanded') === 'true' ? closeMenu() : openMenu(menuButton);
      return;
    }
    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger) { openCart(cartTrigger); return; }
    if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-scrim]')) { closeCart(); return; }
    const changer = event.target.closest('[data-cart-change]');
    if (changer) { changeCartLine(changer); return; }
    const thumb = event.target.closest('[data-media-target], [data-fallback-image]');
    if (thumb) {
      const gallery = thumb.closest('[data-product-gallery]');
      gallery?.querySelectorAll('.product-thumb').forEach((item) => { item.classList.toggle('is-active', item === thumb); item.setAttribute('aria-pressed', item === thumb ? 'true' : 'false'); });
      if (thumb.dataset.mediaTarget) gallery?.querySelectorAll('[data-media-id]').forEach((media) => { const active = media.dataset.mediaId === thumb.dataset.mediaTarget; media.hidden = !active; media.classList.toggle('is-active', active); });
      if (thumb.dataset.fallbackImage) { const image = gallery?.querySelector('.product-media-stage img'); if (image) image.src = thumb.dataset.fallbackImage; }
    }
  });

  doc.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  doc.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const root = select.closest('[data-product-root]');
    const option = select.options[select.selectedIndex];
    const available = option.dataset.available === 'true';
    const price = root?.querySelector('[data-product-price]');
    const compare = root?.querySelector('[data-product-compare]');
    const saving = root?.querySelector('[data-product-saving]');
    const stock = root?.querySelector('[data-product-stock]');
    const submit = root?.querySelector('[data-add-to-cart]');
    const label = root?.querySelector('[data-add-label]');
    if (price) price.textContent = option.dataset.price;
    if (compare) { compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare; }
    if (saving) { saving.textContent = option.dataset.saving || ''; saving.hidden = !option.dataset.saving; }
    if (stock) {
      const inventory = Number(option.dataset.inventory);
      stock.textContent = !available ? 'Sold out' : option.dataset.managed === 'true' && inventory > 0 ? `${inventory} units available` : 'Available';
    }
    if (submit) submit.disabled = !available;
    if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    window.history.replaceState({}, '', url);
  });

  doc.addEventListener('keydown', (event) => {
    const drawer = doc.querySelector('[data-cart-drawer]:not([hidden])');
    const menu = doc.querySelector('[data-mobile-menu]:not([hidden])');
    if (event.key === 'Escape') { if (drawer) closeCart(); else if (menu) closeMenu(); }
    if (drawer) trapFocus(event, drawer);
    if (menu) trapFocus(event, menu);
  });

  const setupObservers = () => {
    const revealItems = doc.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (window.Align?.motionEnabled === false || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      revealItems.forEach((item) => observer.observe(item));
    }
  };

  const updateScroll = () => {
    const max = doc.documentElement.scrollHeight - window.innerHeight;
    const progress = max > 0 ? window.scrollY / max : 0;
    doc.querySelector('[data-scroll-progress]')?.style.setProperty('transform', `scaleX(${progress})`);
  };

  setupObservers();
  updateScroll();
  window.addEventListener('scroll', updateScroll, { passive: true });
  doc.addEventListener('shopify:section:load', setupObservers);
})();
